import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ModelSelector from '../components/ModelSelector';
import { Model, Brand } from '../types/BookingTypes';
import { getBrands } from '../services/brandService';
import { getModelsByBrandId } from '../services/modelService';
import { generateSEOUrl } from '../utils/seoUtils';

const ModelPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();

  const [brand, setBrand] = useState<Brand | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);

  useEffect(() => {
    
    if (!brandId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      getBrands(),
      getModelsByBrandId(Number(brandId))
    ])
      .then(([brands, models]) => {
        const foundBrand = brands.find(b => b.id === Number(brandId));
        if (!foundBrand) throw new Error('Brand not found');
        setBrand(foundBrand);
        setModels(models);
      })
      .catch((err) => setError(err.message || 'Failed to load data'))
      .finally(() => setLoading(false));
  }, [brandId]);

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minto-500 mx-auto"></div>
        <div className="mt-4 text-gray-500">Loading...</div>
      </div>
    );
  }
  if (error || !brand) {
    return <div className="p-8 text-center text-red-500">{error || 'Brand not found.'}</div>;
  }

  const filteredModels = models.filter(
    (model) => model.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBack = () => navigate('/');

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model);
    // Navigate to SEO-friendly service URL and pass the model object in state
    const seoUrl = generateSEOUrl(model);
    navigate(seoUrl, { state: { model } });
  };

  return (
    <div className="p-2">
      <ModelSelector
        models={filteredModels}
        selectedModel={selectedModel}
        onSelect={handleModelSelect}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
        onBack={handleBack}
        brandName={brand.name}
      />
    </div>
  );
};

export default ModelPage; 